import * as React from "react";
import { AutoQuoteSettings, Branch } from "gen/clients";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { formatPriceWithCurrency } from "utils/currencyUtils";

interface Props {
  open: boolean;
  branch: Branch;
  currentSettings: AutoQuoteSettings;
  onClose: () => void;
  onConfirm: (branchId: string) => Promise<void>;
}

const DeleteBranchSettingsDialog: React.FC<Props> = ({ open, branch, currentSettings, onClose, onConfirm }) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleConfirm = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await onConfirm(branch.id);
      onClose(); // Only close on success
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred while deleting the settings");
    } finally {
      setIsLoading(false);
    }
  };

  // Reset error when dialog opens
  React.useEffect(() => {
    if (open) {
      setError(null);
      setIsLoading(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete Branch Auto-Quote Settings</DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete the custom auto-quote settings for this branch?
          </Typography>

          <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Branch:</strong> {branch.name} ({branch.id})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <strong>Current Custom Settings:</strong>
            </Typography>
            <Box sx={{ ml: 2, mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                • Price Per Page: {formatPriceWithCurrency(currentSettings.pricePerPage, branch.currency)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Page Count Escalation: {currentSettings.pageCountEscalationThreshold} pages
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Quote Expiration: {currentSettings.quoteExpiration} days
              </Typography>
            </Box>
          </Box>

          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The branch will revert to using the default auto-quote settings.
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
        >
          {isLoading ? "Deleting..." : "Delete Settings"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { DeleteBranchSettingsDialog };
